/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  Text,
  useNumberInput,
} from '@chakra-ui/react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { WorkflowObjectType, WorkflowType } from '@/client/services/api/graphql/gql/graphql';

import { AssignmentFormData } from '../../../../types/assignmentTypes';
import WarmNeutralSelectionMenu from '../WarmNeutralSelectionMenu';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function HiredDaysAgoForm() {
  const { watch, setValue } = useFormContext<AssignmentFormData>();
  const { t } = useTranslation();
  const { settings } = watch();
  const { type, objectNumber } = settings;

  useEffect(() => {
    setValue('settings.objectType', WorkflowObjectType.User);
  }, []);

  const hiredRangeOptions = [
    {
      label: t('admin.assignments.automations.hired.hiredToday.label'),
      value: WorkflowType.HiredToday,
      onClick: () => {
        setValue('settings.type', WorkflowType.HiredToday);
      },
    },
    {
      label: t('admin.assignments.automations.hired.hired60.label'),
      value: WorkflowType.Hired_60,
      onClick: () => {
        setValue('settings.type', WorkflowType.Hired_60);
      },
    },
    {
      label: t('admin.assignments.automations.hired.hired90.label'),
      value: WorkflowType.Hired_90,
      onClick: () => {
        setValue('settings.type', WorkflowType.Hired_90);
      },
    },
    {
      label: t('admin.assignments.automations.hired.custom.label'),
      value: WorkflowType.HiredCustom,
      onClick: () => {
        setValue('settings.type', WorkflowType.HiredCustom);
      },
    },
  ];

  const getLabelFromHiredRangeType = (hiredRangeType: WorkflowType) =>
    hiredRangeOptions.find((option) => option.value === hiredRangeType)?.label ||
    t('admin.assignments.automations.hiredDaysAgo.selectRange');

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } = useNumberInput({
    defaultValue: objectNumber || 1,
    min: 0,
    max: 10000,
  });

  const incDays = getIncrementButtonProps();
  const decDays = getDecrementButtonProps();
  const inputDays = getInputProps();

  useEffect(() => {
    const currentDays = objectNumber;
    const daysInput = inputDays['aria-valuenow'];

    if (typeof daysInput !== 'number') return;

    if (currentDays !== daysInput) {
      setValue('settings.objectNumber', daysInput);
    }
  }, [inputDays]);

  return (
    <>
      <WarmNeutralSelectionMenu
        label={t('admin.assignments.automations.hiredDaysAgo.selectRange')}
        buttonLabel={getLabelFromHiredRangeType(type)}
        options={hiredRangeOptions}
      />

      {type === WorkflowType.HiredCustom && (
        <>
          <Divider />

          <Flex justifyContent="space-between" alignItems="center">
            <Text variant="createLabel">
              {t('admin.assignments.automations.hired.custom.daysAfterHired')}
            </Text>

            <HStack>
              <IconButton
                {...decDays}
                size="sm"
                variant="createNumberStepper"
                isRound
                icon={<FaMinus />}
                aria-label={t('admin.assignments.audience.additionalFilters.decreaseDays')}
              />

              <Box width={16}>
                <Input {...inputDays} variant="createNumber" fontSize="14px" fontWeight={600} />
              </Box>

              <IconButton
                {...incDays}
                size="sm"
                variant="createNumberStepper"
                isRound
                icon={<FaPlus />}
                aria-label={t('admin.assignments.audience.additionalFilters.increaseDays')}
              />
            </HStack>
          </Flex>
        </>
      )}
    </>
  );
}
